"use client";

import { classed, clsx } from "@frend-digital/ui";
import styles from "./Popover.module.css";
import {
	createContext,
	use,
	useContext,
	useId,
	useMemo,
	useRef,
	useState,
	type ComponentProps,
	type RefObject,
} from "react";
import { Slot } from "../Slot";
import { useHydrated, useMediaQuery } from "@frend-digital/ui/hooks";
import { Anchor } from "../Anchor";

interface PopoverContextValue {
	popoverId: string;
	open: boolean;
	setOpen: (open: boolean) => void;
	ref: RefObject<HTMLDivElement | null>;
}

const PopoverContext = createContext<PopoverContextValue>(null!);

const toAnchorName = (id: string) => `--popover-anchor-${id.replace(/[^a-zA-Z0-9-]/g, "")}`;

export const PopoverRoot = ({ children }: { children: React.ReactNode }) => {
	const id = useId();

	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null!);

	return (
		<PopoverContext.Provider value={{ popoverId: id, open, setOpen, ref }}>
			{children}
		</PopoverContext.Provider>
	);
};

export const usePopover = () => {
	const context = useContext(PopoverContext);
	if (!context) {
		throw new Error("usePopover must be used within a PopoverRoot");
	}
	return context;
};
export const PopoverContent = ({
	children,
	className,
	asChild,
	relativeAnchor,
	...props
}: ComponentProps<"div"> & {
	asChild?: boolean;
	relativeAnchor?: boolean;
}) => {
	const { popoverId, open, setOpen, ref: popoverRef } = use(PopoverContext);

	const Component = asChild ? Slot : "div";

	const isMobile = useMediaQuery("(max-width: 1024px)");

	const hydrated = useHydrated();

	// Check if the browser supports the native Popover API
	const supportsPopover = useMemo(
		() => hydrated && typeof HTMLElement !== "undefined" && "popover" in HTMLElement.prototype,
		[hydrated],
	);

	// Check if the browser supports CSS anchor positioning
	const supportsAnchorPositioning = useMemo(
		() =>
			hydrated &&
			typeof CSS !== "undefined" &&
			typeof CSS.supports === "function" &&
			CSS.supports("anchor-name", "--test"),
		[hydrated],
	);

	// Use fallback if popover API is not supported, on mobile, or if anchor positioning is not supported
	if (hydrated && (!supportsPopover || isMobile || !supportsAnchorPositioning)) {
		return (
			<Component
				ref={(el: HTMLDivElement | null) => {
					popoverRef.current = el;
					if (el) {
						const controller = new AbortController();
						document.body.addEventListener(
							"click",
							(e) => {
								const target = e.target as HTMLElement;
								if (e.target === el || el.contains(target)) {
									// WE ARE INSIDE
									setOpen(true);
								} else {
									setOpen(false);
								}
							},
							{ signal: controller.signal },
						);

						return () => {
							controller.abort();
						};
					}
				}}
				{...props}
				className={clsx(styles.Popover, className, relativeAnchor && styles.useRelativeAnchor)}
				data-popover-open={open ? "open" : "closed"}
			>
				{children}
			</Component>
		);
	}

	return (
		<Component
			ref={popoverRef}
			popover="auto"
			{...props}
			id={popoverId}
			className={clsx(styles.Popover, className)}
			style={{ positionAnchor: toAnchorName(popoverId), ...props.style } as React.CSSProperties}
		>
			{children}
		</Component>
	);
};

export const PopoverTrigger = ({
	asChild,
	children,
	...props
}: ComponentProps<"button"> & {
	asChild?: boolean;
}) => {
	const Component = asChild ? Slot : "button";
	const { popoverId, setOpen, ref } = use(PopoverContext);

	return (
		<Anchor asChild containerSelector={`#${popoverId}`} containerRef={ref}>
			<Component
				{...props}
				onClick={(e: React.MouseEvent) => {
					setOpen(true);
					if (props.onClick) {
						props.onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
					}
				}}
				popoverTarget={popoverId}
				popoverTargetAction="toggle"
				style={{ anchorName: toAnchorName(popoverId), ...props.style } as React.CSSProperties}
			>
				{children}
			</Component>
		</Anchor>
	);
};

export const PopoverClose = ({
	asChild,
	children,
	...props
}: ComponentProps<"button"> & {
	asChild?: boolean;
}) => {
	const Component = asChild ? Slot : "button";
	const { popoverId, setOpen } = use(PopoverContext);

	return (
		<Component
			{...props}
			onClick={() => setOpen(false)}
			popoverTarget={popoverId}
			popoverTargetAction="hide"
		>
			{children}
		</Component>
	);
};

export const PopoverCloseIcon = classed(PopoverClose, styles.Close);

export const PopoverFooter = classed.footer(styles.Footer);
